import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { VariablePill } from './VariablePill';

/**
 * Inline atom node for a supported `{{variable}}` placeholder.
 *
 * Modeled on the existing SurveyNode. Rendering it as a real node (rather than
 * a decoration painted over editable text) makes the placeholder:
 *   - non-editable — users can select/delete it whole but not type inside it;
 *   - faithfully spaced — the pill has no fake horizontal padding (ENG-3184).
 *
 * Serialization stays as the plain `{{name}}` token:
 *   - `renderText` drives `getText()` AND clipboard copy → subjects and copied
 *     pills come out as `{{name}}` with no extra code;
 *   - `renderHTML` emits an EMPTY `<span data-kvariable="name">`, which the body
 *     editor converts back to `{{name}}` via `serializeVariables()` before it
 *     reaches formik/backend (mirrors how SurveyNode round-trips).
 */
export const VariableNode = Node.create({
  name: 'variable',

  group: 'inline',

  inline: true,

  atom: true,

  selectable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-kvariable'),
        renderHTML: (attributes) =>
          attributes.id ? { 'data-kvariable': attributes.id } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-kvariable]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'variable-pill' }),
    ];
  },

  // Drives editor.getText() and clipboard serialization so the token always
  // comes back out as `{{name}}`.
  renderText({ node }) {
    return `{{${node.attrs.id}}}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariablePill);
  },

  addCommands() {
    return {
      insertVariable:
        (id) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { id } }),
    };
  },

  addKeyboardShortcuts() {
    // Two-step delete: the first Backspace/Delete next to a pill SELECTS the
    // whole node (so the user sees what they're about to remove); a second
    // press then falls through to the base keymap's `deleteSelection`, which
    // removes it. This guards against accidentally nuking a placeholder with a
    // single keystroke.
    const selectAdjacentVariable = (direction) => () => {
      const { state } = this.editor;
      const { $anchor, empty } = state.selection;
      // A node is already selected (e.g. the pill picked on the previous
      // press) — let the default deleteSelection handle the actual removal.
      if (!empty) return false;

      const adjacent =
        direction === 'before' ? $anchor.nodeBefore : $anchor.nodeAfter;
      if (!adjacent || adjacent.type.name !== this.name) return false;

      const pos =
        direction === 'before' ? $anchor.pos - adjacent.nodeSize : $anchor.pos;
      return this.editor.commands.setNodeSelection(pos);
    };

    return {
      Backspace: selectAdjacentVariable('before'),
      Delete: selectAdjacentVariable('after'),
    };
  },
});

export default VariableNode;
